import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import TasksAPI from 'dashboard/api/pilot/tasks';
import { usePilotConfigStore } from 'dashboard/store/pilot/preferences';

export function usePilot() {
  const store = useStore();
  const { t } = useI18n();
  const { isCloudFeatureEnabled, currentAccount } = useAccount();
  const { isEnterprise } = useConfig();
  const uiFlags = useMapGetter('accounts/getUIFlags');
  const currentChat = useMapGetter('getSelectedChat');
  const accountId = useMapGetter('getCurrentAccountId');
  const replyMode = useMapGetter('draftMessages/getReplyEditorMode');
  // conversationId 用数据库主键 id，用于 draftKey
  // 注意：API 返回的 id 字段值就是 display_id，所以这里需要用原始的数据库 id
  const conversationId = computed(() => currentChat.value?.id);
  // conversationDisplayId 用于 API 调用
  // API 返回的 id 字段值就是 display_id，所以直接用 id
  const conversationDisplayId = computed(() => currentChat.value?.id);
  const draftKey = computed(
    () => `draft-${conversationId.value}-${replyMode.value}`
  );
  const draftMessage = useFunctionGetter('draftMessages/get', draftKey);

  // === Feature Flags ===
  const pilotEnabled = computed(() => {
    return isCloudFeatureEnabled(FEATURE_FLAGS.PILOT);
  });

  const pilotTasksEnabled = computed(() => {
    return isCloudFeatureEnabled(FEATURE_FLAGS.PILOT_TASKS);
  });

  // === Pilot Config Store (Feature toggles) ===
  const pilotConfigStore = usePilotConfigStore();
  const { features: pilotFeatures, uiFlags: configUiFlags } =
    storeToRefs(pilotConfigStore);

  // Auto-fetch config if not already loaded
  onMounted(() => {
    if (
      !configUiFlags.value.isFetching &&
      !Object.keys(pilotFeatures.value).length
    ) {
      pilotConfigStore.fetch();
    }
  });

  // Check if a specific Pilot feature is enabled
  const isPilotFeatureEnabled = featureKey => {
    return computed(() => {
      // First check if pilot tasks are enabled globally
      if (!pilotTasksEnabled.value) return false;
      // Then check the specific feature toggle
      return pilotFeatures.value?.[featureKey]?.enabled === true;
    });
  };

  // Label suggestion feature
  const isLabelSuggestionEnabled = isPilotFeatureEnabled('label_suggestion');

  // === Limits (Enterprise) ===
  const pilotLimits = computed(() => {
    return currentAccount.value?.limits?.pilot;
  });

  const documentLimits = computed(() => {
    if (pilotLimits.value?.documents) {
      return useCamelCase(pilotLimits.value.documents);
    }
    return null;
  });

  const responseLimits = computed(() => {
    if (pilotLimits.value?.responses) {
      return useCamelCase(pilotLimits.value.responses);
    }
    return null;
  });

  const isFetchingLimits = computed(() => uiFlags.value.isFetchingLimits);

  const fetchLimits = () => {
    if (isEnterprise) {
      store.dispatch('accounts/limits');
    }
  };

  // === Error Handling ===
  /**
   * Handles API errors and displays appropriate error messages.
   * Silently returns for aborted requests.
   * @param {Error} error - The error object from the API call.
   */
  const handleAPIError = error => {
    if (error.name === 'AbortError' || error.name === 'CanceledError') {
      return;
    }
    const errorMessage =
      error.response?.data?.error ||
      t('INTEGRATION_SETTINGS.OPEN_AI.GENERATE_ERROR');
    useAlert(errorMessage);
  };

  // === Task Methods ===
  /**
   * Rewrites content with a specific operation.
   * @param {string} content - The content to rewrite.
   * @param {string} operation - The operation (fix_spelling_grammar, casual, professional, expand, shorten, improve, etc).
   * @param {Object} [options={}] - Additional options.
   * @param {AbortSignal} [options.signal] - AbortSignal to cancel the request.
   * @returns {Promise<{message: string, followUpContext?: Object}>} The rewritten content and optional follow-up context.
   */
  const rewriteContent = async (content, operation, options = {}) => {
    try {
      const result = await TasksAPI.rewrite(
        {
          content: content || draftMessage.value,
          operation,
          conversationDisplayId: conversationDisplayId.value,
        },
        options.signal
      );
      const { message: generatedMessage, follow_up_context: followUpContext } =
        result.data;
      return { message: generatedMessage, followUpContext };
    } catch (error) {
      if (error?.response?.status === 402) {
        return {
          message: '',
          error:
            error.response.data?.error || 'Token 余额不足，请联系管理员充值',
        };
      }
      handleAPIError(error);
      return { message: '' };
    }
  };

  /**
   * Summarizes a conversation.
   * @param {Object} [options={}] - Additional options.
   * @param {AbortSignal} [options.signal] - AbortSignal to cancel the request.
   * @returns {Promise<{message: string, followUpContext?: Object}>} The summary and optional follow-up context.
   */
  const summarizeConversation = async (options = {}) => {
    try {
      const result = await TasksAPI.summarize(
        conversationDisplayId.value,
        options.signal
      );
      const { message: generatedMessage, follow_up_context: followUpContext } =
        result.data;
      return { message: generatedMessage, followUpContext };
    } catch (error) {
      if (error?.response?.status === 402) {
        return {
          message: '',
          error:
            error.response.data?.error || 'Token 余额不足，请联系管理员充值',
        };
      }
      handleAPIError(error);
      return { message: '' };
    }
  };

  /**
   * Gets a reply suggestion for the current conversation.
   * @param {Object} [options={}] - Additional options.
   * @param {AbortSignal} [options.signal] - AbortSignal to cancel the request.
   * @returns {Promise<{message: string, followUpContext?: Object}>} The reply suggestion and optional follow-up context.
   */
  const getReplySuggestion = async (options = {}) => {
    try {
      const result = await TasksAPI.replySuggestion(
        conversationDisplayId.value,
        options.signal
      );
      const { message: generatedMessage, follow_up_context: followUpContext } =
        result.data;
      return { message: generatedMessage, followUpContext };
    } catch (error) {
      if (error?.response?.status === 402) {
        return {
          message: '',
          error:
            error.response.data?.error || 'Token 余额不足，请联系管理员充值',
        };
      }
      handleAPIError(error);
      return { message: '' };
    }
  };

  /**
   * Sends a follow-up message to refine a previous AI task result.
   * @param {Object} options - The follow-up options.
   * @param {Object} options.followUpContext - The follow-up context from a previous task.
   * @param {string} options.message - The follow-up message/request from the user.
   * @param {AbortSignal} [options.signal] - AbortSignal to cancel the request.
   * @returns {Promise<{message: string, followUpContext: Object}>} The follow-up response and updated context.
   */
  const followUp = async ({ followUpContext, message, signal }) => {
    try {
      const result = await TasksAPI.followUp(
        {
          followUpContext,
          message,
          conversationDisplayId: conversationDisplayId.value,
        },
        signal
      );
      const { message: generatedMessage, follow_up_context: updatedContext } =
        result.data;
      return { message: generatedMessage, followUpContext: updatedContext };
    } catch (error) {
      handleAPIError(error);
      return { message: '', followUpContext };
    }
  };

  /**
   * Processes an AI event. Routes to the appropriate method based on type.
   * @param {string} [type='improve'] - The type of AI event to process.
   * @param {string} [content=''] - The content to process.
   * @param {Object} [options={}] - Additional options.
   * @param {AbortSignal} [options.signal] - AbortSignal to cancel the request.
   * @returns {Promise<{message: string, followUpContext?: Object}>} The generated message and optional follow-up context.
   */
  const processEvent = async (type = 'improve', content = '', options = {}) => {
    if (type === 'summarize') {
      return summarizeConversation(options);
    }
    if (type === 'reply_suggestion') {
      return getReplySuggestion(options);
    }
    // All other types are rewrite operations
    return rewriteContent(content, type, options);
  };

  /**
   * Gets label suggestions for the current conversation.
   * @param {Object} [options={}] - Additional options.
   * @param {AbortSignal} [options.signal] - AbortSignal to cancel the request.
   * @returns {Promise<{labels: string[], error?: string}>} An object with labels array and optional error message.
   */
  const getLabelSuggestions = async (options = {}) => {
    try {
      const result = await TasksAPI.labelSuggestion(
        conversationDisplayId.value,
        options.signal
      );

      // 检查是否有错误响应
      if (result.data?.error) {
        return { labels: [], error: result.data.error };
      }

      const { message: labels } = result.data;
      // Clean and normalize labels
      if (!labels) return { labels: [] };
      return {
        labels: labels
          .toLowerCase()
          .split(',')
          .map(label => label.trim())
          .filter(label => label)
          .filter((label, index, self) => self.indexOf(label) === index),
      };
    } catch (error) {
      if (error?.response?.status === 402) {
        return {
          labels: [],
          error:
            error.response.data?.error || 'Token 余额不足，请联系管理员充值',
        };
      }
      handleAPIError(error);
      return { labels: [], error: error.message };
    }
  };

  /**
   * Generates contact notes from the current conversation.
   * @param {Object} [options={}] - Additional options.
   * @param {AbortSignal} [options.signal] - AbortSignal to cancel the request.
   * @returns {Promise<{message: string, notesCount: number, error?: string}>} The generated notes result.
   */
  const generateContactNotes = async (options = {}) => {
    try {
      const result = await TasksAPI.contactNotes(
        conversationDisplayId.value,
        options.signal
      );
      return {
        message: result.data?.message || '',
        notesCount: result.data?.notes_count || 0,
      };
    } catch (error) {
      if (error?.response?.status === 402) {
        return {
          message: '',
          notesCount: 0,
          error:
            error.response.data?.error || 'Token 余额不足，请联系管理员充值',
        };
      }
      handleAPIError(error);
      return { message: '', notesCount: 0, error: error.message };
    }
  };

  return {
    // Feature flags
    pilotEnabled,
    pilotTasksEnabled,
    isLabelSuggestionEnabled,

    // Limits (Enterprise)
    pilotLimits,
    documentLimits,
    responseLimits,
    fetchLimits,
    isFetchingLimits,

    // Conversation context
    draftMessage,
    currentChat,

    // Task methods
    rewriteContent,
    summarizeConversation,
    getReplySuggestion,
    getLabelSuggestions,
    generateContactNotes,
    followUp,
    processEvent,
  };
}
